import { API_URL } from "@/shared/api/api-client";
import { ApiError, request, requestBlob } from "@/shared/api/http";
import type { RawRow } from "./spreadsheet-parse";
import { inParallel, PUBLICATION_PAGE } from "./pages";

/**
 * Cliente HTTP do Versa. Usa o MESMO `request` do resto do front — a
 * autenticação (token ou `X-User-Id` em dev) e o `X-Company-Id` da
 * empresa aberta saem dali, então o escopo por empresa vale aqui sem
 * nenhum código a mais.
 */

/** Uma linha da lista de relatórios — sem a estrutura do editor. */
export type VersaDashboardSummary = {
  id: number;
  name: string;
  template_id: string | null;
  revision: number;
  created_by: string;
  created_at: string;
  updated_by: string;
  updated_at: string;
  /** O nome de hoje no cadastro; ausente pra quem não está nele (a tela
   * cai no código). */
  created_by_name?: string | null;
  updated_by_name?: string | null;
  /** Bases esperando um relatório do Reputaition que ainda está sendo
   * gerado: a lista só libera o relatório quando terminam. */
  preparing_bases?: number;
  /** Relatório ou modelo da empresa (2026-10-30). */
  kind?: VersaDashboardKind;
  /** Decide a janela dos comparativos e quanto o período anda no
   * relatório do dia; `null` no relatório antigo (lê pelo `template_id`). */
  cadence?: VersaCadence | null;
  /** O modelo da empresa de onde o relatório nasceu. */
  source_template_id?: number | null;
  /** O dia (AAAA-MM-DD, Brasília) do relatório do dia criado pelo atalho. */
  daily_date?: string | null;
};

export type VersaDashboardKind = "report" | "template";
export type VersaCadence = "daily" | "weekly" | "monthly" | "free";

/** O relatório inteiro, como o editor abre. `blocks` e `header_config`
 * têm o formato do editor portado do Hub; o servidor só guarda. */
export type VersaDashboard = VersaDashboardSummary & {
  blocks: unknown[];
  header_config: Record<string, unknown>;
};

export type VersaBaseStatus = "receiving" | "queued" | "processing" | "ready" | "failed";

export type VersaBase = {
  id: number;
  dashboard_id: number;
  name: string;
  origin: string;
  tool: string | null;
  status: VersaBaseStatus;
  columns: string[];
  has_classification: boolean;
  total_rows: number;
  valid_rows: number;
  duplicate_rows: number;
  error_rows: number;
  error_message: string | null;
  error_samples: Array<{ row_index: number; message: string }>;
  created_by: string;
  created_at: string;
  finished_at: string | null;
  /** Base de relatório do Reputaition: qual relatório ela lê e como ele
   * está. Ausente nas bases de planilha. */
  report_kind?: VersaReportKind | null;
  report_ref_id?: number | null;
  report?: VersaReportState | null;
};

export type VersaReportKind = "draft" | "saved";

/** `missing`: apagado ou descartado no Reputaition; `expired`: rascunho
 * vencido — nos dois a base fica sem publicações. `preparing`: o relatório
 * ainda está sendo gerado; `failed`: a geração terminou sem relatório
 * (`error_message` da base diz por quê). */
export type VersaReportState = {
  state: "ok" | "expired" | "missing" | "preparing" | "failed";
  label: string | null;
  expires_at: string | null;
  post_count: number;
  generated_at: string | null;
  data_refreshed_at: string | null;
  /** Um "juntar outro filtro" ainda recalcula o rascunho. */
  recalculating?: boolean;
};

/** Uma publicação tratada, no formato do Versa (o do transformer do
 * Hub). Tipada só no que a tela usa hoje; o editor lê o resto. */
export type VersaPublicationData = {
  texto: string;
  data_publicacao: string | null;
  rede_social: string;
  autor: string;
  sentimento: string;
  url: string;
  [field: string]: unknown;
};

export type VersaPublication = {
  id: number;
  base_id: number;
  hidden: boolean;
  data: VersaPublicationData;
  /** Base de relatório do Reputaition: a menção (é por ela que se tira do
   * relatório). */
  mention_id?: number | null;
};

/** `next_cursor` é opaco: a tela só devolve o que recebeu. */
type PublicationPage = { items: VersaPublication[]; next_cursor: string | null };

const json = (body: unknown): RequestInit => ({ body: JSON.stringify(body) });

export function listVersaDashboards(token: string): Promise<VersaDashboardSummary[]> {
  return request<VersaDashboardSummary[]>(API_URL, "/versa/dashboards", {}, token);
}

/** Cria o relatório já com os blocos do modelo escolhido (o modelo é
 * montado no front, que conhece os blocos de cada um). */
/** Duplicar (Hub, 09/09): o layout e a identidade visual, sem base nem
 * dados do período. Devolve a cópia. */
export function duplicateVersaDashboard(token: string, dashboardId: number): Promise<VersaDashboard> {
  return request<VersaDashboard>(API_URL, `/versa/dashboards/${dashboardId}/duplicate`, { method: "POST" }, token);
}

export function createVersaDashboard(
  token: string,
  name: string,
  template: { id: string; blocks: unknown[]; cadence?: VersaCadence | null } | null = null,
): Promise<VersaDashboard> {
  return request<VersaDashboard>(
    API_URL,
    "/versa/dashboards",
    { method: "POST", ...json({ name, template_id: template?.id ?? null, blocks: template?.blocks ?? [], cadence: template?.cadence ?? null }) },
    token,
  );
}

// --- modelos da empresa, atalho pessoal e relatório do dia (2026-10-30) ---

export function listVersaTemplates(token: string): Promise<VersaDashboardSummary[]> {
  return request<VersaDashboardSummary[]>(API_URL, "/versa/templates", {}, token);
}

/** Um modelo novo: do canvas em branco / modelo de fábrica (`blocks`), ou a
 * partir de um relatório ou modelo da empresa (`from_dashboard_id`). */
export function createVersaTemplate(
  token: string,
  body: { name: string; cadence: VersaCadence; from_dashboard_id?: number | null; template_id?: string | null; blocks?: unknown[] },
): Promise<VersaDashboard> {
  return request<VersaDashboard>(API_URL, "/versa/templates", { method: "POST", ...json(body) }, token);
}

export function updateVersaTemplate(token: string, templateId: number, body: { name?: string; cadence?: VersaCadence }): Promise<VersaDashboard> {
  return request<VersaDashboard>(API_URL, `/versa/templates/${templateId}`, { method: "PATCH", ...json(body) }, token);
}

export function deleteVersaTemplate(token: string, templateId: number): Promise<void> {
  return request<void>(API_URL, `/versa/templates/${templateId}`, { method: "DELETE" }, token);
}

/** Os modelos de OUTRA empresa, pra copiar (Máster e Administrador,
 * 2026-11-03). O servidor confere o acesso de quem pede àquela empresa. */
export function listTemplatesOfCompany(token: string, companyId: string): Promise<VersaDashboardSummary[]> {
  return request<VersaDashboardSummary[]>(API_URL, `/versa/templates/from-company/${encodeURIComponent(companyId)}`, {}, token);
}

/** Traz o modelo de outra empresa pra atual, sem dados nem imagens. */
export function copyTemplateFromCompany(
  token: string,
  body: { source_company_id: string; template_id: number; name?: string },
): Promise<VersaDashboard> {
  return request<VersaDashboard>(API_URL, "/versa/templates/copy", { method: "POST", ...json(body) }, token);
}

export function createReportFromTemplate(token: string, templateId: number, name: string): Promise<VersaDashboard> {
  return request<VersaDashboard>(API_URL, `/versa/templates/${templateId}/reports`, { method: "POST", ...json({ name }) }, token);
}

export type TodayReport = {
  dashboard: VersaDashboard;
  /** `false` = o relatório de hoje já existia e só foi reaberto. */
  created: boolean;
  /** Bases do Reputaition pedidas de novo com o período andado. */
  regenerated: number;
  /** Bases de planilha do relatório anterior: precisam do arquivo novo. */
  spreadsheets: string[];
  warnings: string[];
};

export function openTemplateToday(token: string, templateId: number): Promise<TodayReport> {
  return request<TodayReport>(API_URL, `/versa/templates/${templateId}/today`, { method: "POST" }, token);
}

export type VersaShortcut = { template_id: number; name: string; cadence: VersaCadence | null; active: boolean };

/** Avisa o menu lateral que os atalhos mudaram (fixar/desafixar). */
export const SHORTCUTS_CHANGED_EVENT = "versa:shortcuts-changed";

export function listVersaShortcuts(token: string): Promise<VersaShortcut[]> {
  return request<VersaShortcut[]>(API_URL, "/versa/shortcuts", {}, token);
}

export async function setVersaShortcut(token: string, templateId: number, pinned: boolean): Promise<VersaShortcut[]> {
  const rows = await request<VersaShortcut[]>(API_URL, `/versa/shortcuts/${templateId}`, { method: pinned ? "PUT" : "DELETE" }, token);
  window.dispatchEvent(new Event(SHORTCUTS_CHANGED_EVENT));
  return rows;
}

export function getVersaDashboard(token: string, id: number): Promise<VersaDashboard> {
  return request<VersaDashboard>(API_URL, `/versa/dashboards/${id}`, {}, token);
}

export function deleteVersaDashboard(token: string, id: number): Promise<void> {
  return request<void>(API_URL, `/versa/dashboards/${id}`, { method: "DELETE" }, token);
}

export function listVersaBases(token: string, dashboardId: number): Promise<VersaBase[]> {
  return request<VersaBase[]>(API_URL, `/versa/dashboards/${dashboardId}/bases`, {}, token);
}

export function deleteVersaBase(token: string, baseId: number): Promise<void> {
  return request<void>(API_URL, `/versa/bases/${baseId}`, { method: "DELETE" }, token);
}

// --- administração de publicações das bases de planilha (2026-11-03) ---

/** Os campos que a correção alcança (o servidor confere a mesma lista). */
export const EDITABLE_TEXT_FIELDS = ["titulo", "texto", "url", "autor", "rede_social", "tipo_midia", "sentimento"] as const;
export const EDITABLE_NUMBER_FIELDS = ["alcance", "total_interacoes"] as const;
export type PublicationChanges = Partial<
  Record<(typeof EDITABLE_TEXT_FIELDS)[number], string> & Record<(typeof EDITABLE_NUMBER_FIELDS)[number], number | null>
>;

export type AdminPublicationPage = { items: VersaPublication[]; next_after: number | null };

export function searchBasePublications(token: string, baseId: number, query: string, after = 0): Promise<AdminPublicationPage> {
  const params = new URLSearchParams({ q: query, after: String(after) });
  return request<AdminPublicationPage>(API_URL, `/versa/bases/${baseId}/admin/publications?${params}`, {}, token);
}

export function editBasePublication(token: string, baseId: number, publicationId: number, changes: PublicationChanges): Promise<VersaPublication> {
  return request<VersaPublication>(API_URL, `/versa/bases/${baseId}/admin/publications/${publicationId}`, { method: "PATCH", ...json({ changes }) }, token);
}

export function removeBasePublication(token: string, baseId: number, publicationId: number): Promise<void> {
  return request<void>(API_URL, `/versa/bases/${baseId}/admin/publications/${publicationId}`, { method: "DELETE" }, token);
}

/** A planilha enviada, remontada do bronze (mesmas colunas e linhas). */
export function downloadBaseOriginal(token: string, baseId: number): Promise<Blob> {
  return requestBlob(API_URL, `/versa/bases/${baseId}/original`, token);
}

/** Todas as publicações das bases prontas do relatório, página a página
 * (o servidor pagina por cursor: primeiro as bases de planilha, depois as
 * de relatório do Reputaition, lidas na hora). */
export async function listAllVersaPublications(
  token: string,
  dashboardId: number,
  /** Partes (páginas) lidas e o total — pra tela mostrar quanto falta. */
  onProgress?: (done: number, total: number) => void,
): Promise<VersaPublication[]> {
  const { cursors } = await request<{ cursors: string[] }>(
    API_URL,
    `/versa/dashboards/${dashboardId}/publication-cursors?limit=${PUBLICATION_PAGE}`,
    {},
    token,
  );
  const pages = await inParallel(
    cursors,
    cursor =>
      request<PublicationPage>(
        API_URL,
        `/versa/dashboards/${dashboardId}/publications?cursor=${encodeURIComponent(cursor)}&limit=${PUBLICATION_PAGE}`,
        {},
        token,
      ),
    onProgress,
  );
  return pages.flatMap(page => page.items);
}


export function setVersaPublicationHidden(token: string, publicationId: number, hidden: boolean): Promise<void> {
  return request<void>(
    API_URL,
    `/versa/publications/${publicationId}/hidden`,
    { method: "PUT", ...json({ hidden }) },
    token,
  );
}

/** Linhas por lote — o teto do servidor. */
export const ROWS_PER_BATCH = 2000;
/** Lotes enviados ao mesmo tempo: o bastante para não esperar ida e volta
 * lote a lote, pouco o bastante para não disputar com o resto da tela. */
const PARALLEL_BATCHES = 3;
/** Tentativas por lote. Reenviar é seguro: o bronze ignora linha que já
 * chegou, então uma queda no meio não duplica nada. */
const ATTEMPTS_PER_BATCH = 3;

/** Vale tentar de novo só o que pode passar sozinho: rede, 5xx e 429. Um
 * 4xx (lote recusado, base já concluída) daria o mesmo erro de novo. */
function retryable(error: unknown): boolean {
  if (!(error instanceof ApiError)) return true;
  return error.status >= 500 || error.status === 429;
}

export type UploadProgress = { sent: number; total: number };

/**
 * O envio inteiro de uma base: cria, manda os lotes e conclui. O
 * tratamento acontece depois, no worker — quem chama acompanha pelo
 * status da base.
 */
export async function uploadVersaBase(
  token: string,
  dashboardId: number,
  base: { name: string; columns: string[]; rows: RawRow[]; hasClassification: boolean },
  onProgress: (progress: UploadProgress) => void,
): Promise<VersaBase> {
  const created = await request<VersaBase>(
    API_URL,
    `/versa/dashboards/${dashboardId}/bases`,
    {
      method: "POST",
      ...json({ name: base.name, columns: base.columns, has_classification: base.hasClassification }),
    },
    token,
  );
  const total = base.rows.length;
  const starts: number[] = [];
  for (let start = 0; start < total; start += ROWS_PER_BATCH) starts.push(start);
  let sent = 0;
  onProgress({ sent, total });

  const sendBatch = async (start: number) => {
    const rows = base.rows.slice(start, start + ROWS_PER_BATCH).map((values, offset) => ({
      // 1, 2, 3… na ordem do arquivo — é a chave da linha no bronze.
      row_index: start + offset + 1,
      values,
    }));
    for (let attempt = 1; ; attempt++) {
      try {
        await request(API_URL, `/versa/bases/${created.id}/rows`, { method: "POST", ...json({ rows }) }, token);
        break;
      } catch (error) {
        if (attempt >= ATTEMPTS_PER_BATCH || !retryable(error)) throw error;
      }
    }
    sent += rows.length;
    onProgress({ sent, total });
  };

  // Uma fila simples: cada "trilha" pega o próximo lote livre.
  let next = 0;
  const lane = async () => {
    while (next < starts.length) {
      const start = starts[next++]!;
      await sendBatch(start);
    }
  };
  await Promise.all(Array.from({ length: Math.min(PARALLEL_BATCHES, starts.length) }, lane));

  return request<VersaBase>(
    API_URL,
    `/versa/bases/${created.id}/finish`,
    { method: "POST", ...json({ total_rows: total }) },
    token,
  );
}

export type VersaDashboardVersion = {
  id: number;
  revision: number;
  reason: string;
  created_by: string;
  created_at: string;
  created_by_name?: string | null;
};

export type VersaEditLease = {
  holder: string;
  holder_name: string | null;
  expires_at: string;
  mine: boolean;
};

/** Grava o relatório se ninguém tiver salvo depois de `revision` — senão
 * o servidor responde 409 e nada é gravado. `versionReason` ausente =
 * autosave (sem foto na lista de versões). */
export function saveVersaDashboard(
  token: string,
  dashboard: { id: number; revision: number; name: string; blocks: unknown[]; header_config: Record<string, unknown> },
  versionReason: "save" | "checkpoint" | "publish" | null = null,
): Promise<VersaDashboard> {
  return request<VersaDashboard>(
    API_URL,
    `/versa/dashboards/${dashboard.id}`,
    {
      method: "PUT",
      ...json({
        revision: dashboard.revision,
        name: dashboard.name,
        blocks: dashboard.blocks,
        header_config: dashboard.header_config,
        version_reason: versionReason,
      }),
    },
    token,
  );
}

export function listVersaVersions(token: string, dashboardId: number): Promise<VersaDashboardVersion[]> {
  return request<VersaDashboardVersion[]>(API_URL, `/versa/dashboards/${dashboardId}/versions`, {}, token);
}

export function restoreVersaVersion(token: string, dashboardId: number, versionId: number, revision: number): Promise<VersaDashboard> {
  return request<VersaDashboard>(
    API_URL,
    `/versa/dashboards/${dashboardId}/versions/${versionId}/restore`,
    { method: "POST", ...json({ revision }) },
    token,
  );
}

export function acquireVersaLease(token: string, dashboardId: number): Promise<VersaEditLease> {
  return request<VersaEditLease>(API_URL, `/versa/dashboards/${dashboardId}/lease`, { method: "POST" }, token);
}

/** `keepalive`: a liberação sai mesmo quando a aba está fechando. */
export function releaseVersaLease(token: string, dashboardId: number): Promise<void> {
  return request<void>(API_URL, `/versa/dashboards/${dashboardId}/lease`, { method: "DELETE", keepalive: true }, token);
}

/** O link do cliente, como a equipe vê. `path` é o caminho no front
 * (`/share/versa/<código>`); o endereço completo usa o domínio aberto. */
export type VersaShareLink = {
  code: string;
  /** Endereço personalizado (opcional); com ele, `path` usa o endereço. */
  slug?: string | null;
  path: string;
  has_password: boolean;
  /** Se a equipe consegue ler a senha (pra mandar no WhatsApp). */
  password_recoverable?: boolean;
  expires_at: string | null;
  created_by: string;
  created_at: string;
  updated_by: string;
  updated_at: string;
  created_by_name?: string | null;
  updated_by_name?: string | null;
  /** O assistente de IA no link (2026-11-03), desligado por padrão. */
  assistant_enabled?: boolean;
};

/** Liga ou desliga o assistente de IA do link (2026-11-03). */
export function setVersaShareAssistant(token: string, dashboardId: number, enabled: boolean): Promise<VersaShareLink> {
  return request<VersaShareLink>(API_URL, `/versa/dashboards/${dashboardId}/share/assistant`, { method: "PUT", ...json({ enabled }) }, token);
}

export function getVersaShare(token: string, dashboardId: number): Promise<VersaShareLink | null> {
  return request<VersaShareLink | null>(API_URL, `/versa/dashboards/${dashboardId}/share`, {}, token);
}

export function publishVersaShare(
  token: string,
  dashboardId: number,
  options: { password: string | null; expiresAt: string | null; slug?: string | null },
): Promise<VersaShareLink> {
  return request<VersaShareLink>(
    API_URL,
    `/versa/dashboards/${dashboardId}/share`,
    { method: "POST", ...json({ password: options.password, expires_at: options.expiresAt, slug: options.slug ?? null }) },
    token,
  );
}

/** Ajusta senha, validade e endereço personalizado; o código não muda.
 * `password`: `undefined` mantém, `null` tira, texto troca. A validade e o
 * endereço enviados substituem os atuais (`null` = sem prazo / sem
 * endereço). */
export function updateVersaShare(
  token: string,
  dashboardId: number,
  options: { password?: string | null; expiresAt: string | null; slug?: string | null },
): Promise<VersaShareLink> {
  const passwordAction = options.password === undefined ? "keep" : options.password === null ? "clear" : "set";
  return request<VersaShareLink>(
    API_URL,
    `/versa/dashboards/${dashboardId}/share`,
    {
      method: "PUT",
      ...json({
        password_action: passwordAction,
        password: options.password ?? null,
        expires_at: options.expiresAt,
        slug: options.slug ?? null,
      }),
    },
    token,
  );
}

/** A senha do link, só pra equipe (Mostrar/Copiar senha, WhatsApp).
 * `null`: não há senha recuperável. */
export async function revealVersaSharePassword(token: string, dashboardId: number): Promise<string | null> {
  const result = await request<{ password: string | null }>(API_URL, `/versa/dashboards/${dashboardId}/share/password`, {}, token);
  return result.password;
}

export function revokeVersaShare(token: string, dashboardId: number): Promise<void> {
  return request<void>(API_URL, `/versa/dashboards/${dashboardId}/share`, { method: "DELETE" }, token);
}

/** Envia uma imagem (já reduzida no navegador) e devolve a referência
 * `versa-image:<id>` que vai no relatório, mais o endereço pra mostrar
 * agora. O corpo é o arquivo cru; o servidor confere o tipo pelos bytes. */
export function uploadVersaImage(token: string, dashboardId: number, image: Blob): Promise<{ ref: string; url: string }> {
  return request<{ ref: string; url: string }>(API_URL, `/versa/dashboards/${dashboardId}/images`, { method: "POST", body: image }, token);
}

/** Referência → endereço assinado de todas as imagens do relatório. */
export async function listVersaImages(token: string, dashboardId: number): Promise<Record<string, string>> {
  const result = await request<{ images: Record<string, string> }>(API_URL, `/versa/dashboards/${dashboardId}/images`, {}, token);
  return result.images;
}

/** Um envio do relatório por e-mail, como fica no registro. `simulated`:
 * ambiente local sem SendGrid — nada saiu da máquina. */
export type VersaEmailLog = {
  id: number;
  to: string[];
  cc: string[];
  subject: string;
  status: "sent" | "failed" | "simulated";
  error: string | null;
  sent_by: string;
  sent_by_name: string | null;
  sent_at: string;
};

export function sendVersaEmail(
  token: string,
  dashboardId: number,
  email: { to: string[]; cc: string[]; subject: string; html: string },
): Promise<VersaEmailLog> {
  return request<VersaEmailLog>(API_URL, `/versa/dashboards/${dashboardId}/emails`, { method: "POST", ...json(email) }, token);
}

export function listVersaEmails(token: string, dashboardId: number): Promise<VersaEmailLog[]> {
  return request<VersaEmailLog[]>(API_URL, `/versa/dashboards/${dashboardId}/emails`, {}, token);
}

/** O HTML exatamente como foi enviado (a comprovação). */
export async function getVersaEmailHtml(token: string, dashboardId: number, logId: number): Promise<string> {
  const result = await request<{ html: string }>(API_URL, `/versa/dashboards/${dashboardId}/emails/${logId}`, {}, token);
  return result.html;
}

// ── Bases de relatório do Reputaition ───────────────────────────────────
// A base aponta para um relatório CENA do Reputaition (rascunho ou salvo)
// e o servidor lê a foto dele a cada abertura — nada é copiado.

/** Um relatório do Reputaition que pode virar base. */
export type ReportOption = {
  kind: VersaReportKind;
  id: number;
  label: string;
  window_start: string;
  window_end: string;
  post_count: number;
  /** Só rascunho: quando ele vence. */
  expires_at: string | null;
};

export type ReportOptions = { drafts: ReportOption[]; saved: ReportOption[]; saved_has_more: boolean };

/** Os relatórios que podem virar base. `query` busca pelo nome ou pela
 * marca; os salvos vêm em páginas (`savedPage`, a partir de 1). */
export function listReportOptions(token: string, query = "", savedPage = 1): Promise<ReportOptions> {
  const params = new URLSearchParams({ saved_page: String(savedPage) });
  if (query.trim()) params.set("q", query.trim());
  return request(API_URL, `/versa/report-options?${params}`, {}, token);
}

export function addReportBase(token: string, dashboardId: number, kind: VersaReportKind, refId: number): Promise<VersaBase> {
  return request<VersaBase>(
    API_URL,
    `/versa/dashboards/${dashboardId}/report-bases`,
    { method: "POST", ...json({ kind, ref_id: refId }) },
    token,
  );
}

/** "Enviar para o Versa" num pedido só (2026-10-28): o servidor pede o
 * relatório do recorte ao Reputaition (sem o aviso de fim de lá) e cria a
 * base "preparando", habilitada quando o cálculo termina (já salva, se
 * `keep` for `saved`). Quem avisa no sino é o Versa. `recorte` é o corpo
 * do `POST /social-reports/runs`: janela + `recorteToPayload`. */
export function requestVersaReportBase(
  token: string,
  dashboardId: number,
  recorte: Record<string, unknown>,
  keep: VersaReportKind,
  name: string | null,
): Promise<VersaBase> {
  return request<VersaBase>(
    API_URL,
    `/versa/dashboards/${dashboardId}/report-bases/request`,
    { method: "POST", ...json({ recorte, keep, name }) },
    token,
  );
}

/** Salva o rascunho no Reputaition; a base passa a ler o salvo. */
export function saveReportBase(token: string, baseId: number, name: string | null): Promise<VersaBase> {
  return request<VersaBase>(API_URL, `/versa/bases/${baseId}/save-report`, { method: "POST", ...json({ name }) }, token);
}

/** Junta as publicações do filtro de Menções (`mentionsQuery`, a
 * querystring da tela de lá) ao rascunho da base e recalcula o CENA.
 * Devolve a execução do Reputaition, que se acompanha como a geração
 * de relatório. */
export async function addMentionsToBase(token: string, baseId: number, mentionsQuery: URLSearchParams): Promise<number> {
  const result = await request<{ run_id: number }>(
    API_URL,
    `/versa/bases/${baseId}/add-mentions?${mentionsQuery}`,
    { method: "POST" },
    token,
  );
  return result.run_id;
}

export function setReportMentionHidden(token: string, baseId: number, mentionId: number, hidden: boolean): Promise<void> {
  return request<void>(
    API_URL,
    `/versa/bases/${baseId}/mentions/${mentionId}/hidden`,
    { method: "PUT", ...json({ hidden }) },
    token,
  );
}

// ── Análise por IA (editor) ─────────────────────────────────────────────

export type VersaAnalysis = {
  content: string;
  left_content: string | null;
  right_content: string | null;
  /** SWOT (quadrante -> texto) e picos (chave -> texto). */
  sections?: Record<string, string> | null;
};

/** Gera (ou refina) o texto com a IA do Reputaition, na cota por pessoa.
 * O prompt é montado no servidor a partir deste contexto. */
export function generateVersaAnalysis(token: string, dashboardId: number, payload: unknown): Promise<VersaAnalysis> {
  return request<VersaAnalysis>(API_URL, `/versa/dashboards/${dashboardId}/ai/analysis`, { method: "POST", ...json(payload) }, token);
}

/** O prompt montado, pra Emergência IA (copiar e usar noutra IA). Não
 * conta na cota. */
export async function getVersaAnalysisPrompt(token: string, dashboardId: number, payload: unknown): Promise<string> {
  const result = await request<{ prompt: string }>(API_URL, `/versa/dashboards/${dashboardId}/ai/prompt`, { method: "POST", ...json(payload) }, token);
  return result.prompt;
}

/** A IA própria do Versa (Administração → Modelos de IA, seção do
 * Versa): chave e modelo separados dos do Copiloto. A chave nunca volta —
 * só o mascarado. `null` = nunca cadastrada. */
export type VersaAiSetting = {
  api_key_hint: string;
  model_id: number;
  model_name: string | null;
  is_active: boolean;
  unreadable: boolean;
  updated_by: string;
  updated_by_name?: string | null;
  updated_at: string;
};

export function getVersaAiSetting(token: string): Promise<VersaAiSetting | null> {
  return request<VersaAiSetting | null>(API_URL, "/versa/admin/ai", {}, token);
}

/** `api_key` ausente mantém a chave gravada. */
export function saveVersaAiSetting(token: string, payload: { api_key?: string; model_id: number }): Promise<VersaAiSetting> {
  return request<VersaAiSetting>(API_URL, "/versa/admin/ai", { method: "PUT", ...json(payload) }, token);
}

export function deactivateVersaAiSetting(token: string): Promise<void> {
  return request<void>(API_URL, "/versa/admin/ai", { method: "DELETE" }, token);
}

/** Uma instrução da biblioteca da IA ("Histórico / Prompts"). */
export type PromptFragment = { id: number; text: string };
export type PromptLibraryData = { fragments: PromptFragment[]; last_used: PromptFragment | null };

/** A biblioteca de instruções de quem está logado (por pessoa). */
export function getPromptLibrary(token: string): Promise<PromptLibraryData> {
  return request<PromptLibraryData>(API_URL, "/versa/prompt-fragments", {}, token);
}

export function savePromptFragment(token: string, text: string): Promise<PromptLibraryData> {
  return request<PromptLibraryData>(API_URL, "/versa/prompt-fragments", { method: "POST", ...json({ text }) }, token);
}

export function removePromptFragment(token: string, id: number): Promise<PromptLibraryData> {
  return request<PromptLibraryData>(API_URL, `/versa/prompt-fragments/${id}`, { method: "DELETE" }, token);
}

export function markPromptFragmentUsed(token: string, id: number): Promise<void> {
  return request<void>(API_URL, `/versa/prompt-fragments/${id}/used`, { method: "POST" }, token);
}

export type VersaAiStatus = { available: boolean; reason: string | null };

/** Se a IA do Versa pode gerar agora; sem ela, o motivo (e onde configurar). */
export function getVersaAiStatus(token: string): Promise<VersaAiStatus> {
  return request<VersaAiStatus>(API_URL, "/versa/ai/status", {}, token);
}


// ── Sentinela (2026-11) ─────────────────────────────────────────────────

/** Se o Sentinela está ligado neste ambiente (`VERSA_SENTINEL_ENABLED`). */
export function getSentinelStatus(token: string): Promise<{ enabled: boolean }> {
  return request<{ enabled: boolean }>(API_URL, "/versa/sentinel/status", {}, token);
}

export type SentinelAlert = {
  id: number;
  dashboard_id: number | null;
  source: string;
  model: string;
  period: string;
  total_publications: number;
  text: string;
  is_default: boolean;
  created_by: string;
  created_at: string;
};

export function listSentinelAlerts(token: string): Promise<SentinelAlert[]> {
  return request<SentinelAlert[]>(API_URL, "/versa/sentinel/alerts", {}, token);
}

export type SentinelAlertSave = {
  dashboard_id: number | null;
  source: string;
  model: string;
  period: string;
  total_publications: number;
  text: string;
  is_default: boolean;
};

export function saveSentinelAlert(token: string, alert: SentinelAlertSave): Promise<SentinelAlert> {
  return request<SentinelAlert>(API_URL, "/versa/sentinel/alerts", { method: "POST", ...json(alert) }, token);
}

export function setSentinelDefault(token: string, id: number): Promise<void> {
  return request<void>(API_URL, `/versa/sentinel/alerts/${id}/default`, { method: "POST" }, token);
}

export function deleteSentinelAlert(token: string, id: number): Promise<void> {
  return request<void>(API_URL, `/versa/sentinel/alerts/${id}`, { method: "DELETE" }, token);
}

export type SentinelProfile = { prompt_single: string; prompt_multi: string; default_single: string; default_multi: string };

export function getSentinelProfile(token: string): Promise<SentinelProfile> {
  return request<SentinelProfile>(API_URL, "/versa/sentinel/profile", {}, token);
}

export function saveSentinelProfile(token: string, profile: { prompt_single: string; prompt_multi: string }): Promise<SentinelProfile> {
  return request<SentinelProfile>(API_URL, "/versa/sentinel/profile", { method: "PUT", ...json(profile) }, token);
}

/** O pedido do alerta (o servidor monta o texto pra IA com isto). */
export type SentinelAlertRequest = {
  dashboard_id: number | null;
  company_name: string;
  source: string;
  period: string;
  totals: Record<string, unknown>;
  sample: Record<string, string | number | null>[];
  manual: Record<string, string | number | null>[];
  model_mode: "new" | "existing";
  template: string;
  pieces: string[];
  existing_text: string;
  compare_alert_id: number | null;
};

export function generateSentinelAlert(token: string, body: SentinelAlertRequest): Promise<{ text: string }> {
  return request<{ text: string }>(API_URL, "/versa/sentinel/generate", { method: "POST", ...json(body) }, token);
}

/** O pedido montado, pra baixar e usar numa IA de fora (deixa rastro). */
export function sentinelAlertPrompt(token: string, body: SentinelAlertRequest): Promise<{ prompt: string }> {
  return request<{ prompt: string }>(API_URL, "/versa/sentinel/prompt", { method: "POST", ...json(body) }, token);
}

export type TrendBlock = { time: string; trends: string[] };

export function listTrends(token: string): Promise<TrendBlock[]> {
  return request<TrendBlock[]>(API_URL, "/versa/sentinel/trends", {}, token);
}

export type TrendingRequest = { terms: { term: string; position: string }[]; multi: boolean };

export function generateTrending(token: string, body: TrendingRequest): Promise<{ raw: string; summaries: string[] }> {
  return request<{ raw: string; summaries: string[] }>(API_URL, "/versa/sentinel/trending/generate", { method: "POST", ...json(body) }, token);
}

export function trendingPrompt(token: string, body: TrendingRequest): Promise<{ prompt: string }> {
  return request<{ prompt: string }>(API_URL, "/versa/sentinel/trending/prompt", { method: "POST", ...json(body) }, token);
}

export type ShortLink = { id: number; code: string; url: string; clicks: number; created_at: string; disabled: boolean };

export function createShortLink(token: string, url: string): Promise<ShortLink> {
  return request<ShortLink>(API_URL, "/versa/short-links", { method: "POST", ...json({ url }) }, token);
}

export function listShortLinks(token: string): Promise<ShortLink[]> {
  return request<ShortLink[]>(API_URL, "/versa/short-links", {}, token);
}

export function disableShortLink(token: string, id: number): Promise<ShortLink> {
  return request<ShortLink>(API_URL, `/versa/short-links/${id}/disable`, { method: "POST" }, token);
}

/** O endereço público de um código curto (`/l/<code>`), no mesmo site. */
export function shortLinkUrl(code: string, origin = window.location.origin): string {
  return `${origin}/l/${code}`;
}
